import { useRef } from "react";

/** A sideways flick: this far, mostly sideways, this quick. Under any of the three it
 *  is a scroll, a tap, or a rest — and none of those changes the channel. */
const SWIPE_MIN_PX = 64;
const SWIPE_RATIO = 1.5;
const SWIPE_MAX_MS = 800;
/** A click this soon after a moved mouse release is the drag's own, and is dropped. */
const CLICK_AFTER_DRAG_MS = 250;

/**
 * A finger flicking sideways across an element — the phone's Left and Right arrows —
 * and, since 2026-09-09, a MOUSE pulling sideways too: the desk gets the same gesture
 * as the phone, a grab and a pull, and the keys stay for those who have them.
 *
 * Pointer events. The element this is spread onto must carry `touch-action: pan-y`, or
 * the browser claims a sideways finger as a pan and fires pointercancel before it lifts
 * — with pan-y only the vertical axis is the browser's (a snap scroll still scrolls) and
 * a horizontal move reaches pointerup here. Nothing is decided until the pointer lifts,
 * so a tile's own tap contract (pointerdown + click) is untouched: a flick moves too far
 * to produce a click under a finger — but a MOUSE still gets one after press-move-
 * release on the same element, so the click trailing a moved drag is swallowed in the
 * capture phase (`onClickCapture`, spread with the rest), inside a short window.
 *
 * `onSwipe(1)` for a pull LEFT — the content moves on, the same as pressing the right
 * arrow — and `-1` for a pull right.
 */
export function useHorizontalSwipe(onSwipe: (dir: 1 | -1) => void): {
  onPointerDown: React.PointerEventHandler;
  onPointerUp: React.PointerEventHandler;
  onPointerCancel: React.PointerEventHandler;
  onClickCapture: React.MouseEventHandler;
} {
  const start = useRef<{ id: number; x: number; y: number; at: number; mouse: boolean } | null>(null);
  const swallowUntil = useRef(0);
  const onSwipeRef = useRef(onSwipe);
  onSwipeRef.current = onSwipe;
  return {
    onPointerDown: (e) => {
      const mouse = e.pointerType === "mouse";
      if (mouse && e.button !== 0) {
        start.current = null;
        return;
      }
      start.current = { id: e.pointerId, x: e.clientX, y: e.clientY, at: performance.now(), mouse };
    },
    onPointerUp: (e) => {
      const s = start.current;
      start.current = null;
      if (!s || s.id !== e.pointerId) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      // A mouse that moved is not a click, whatever it decides below.
      if (s.mouse && Math.abs(dx) + Math.abs(dy) >= 8) swallowUntil.current = performance.now() + CLICK_AFTER_DRAG_MS;
      if (performance.now() - s.at > SWIPE_MAX_MS) return;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;
      onSwipeRef.current(dx < 0 ? 1 : -1);
    },
    onPointerCancel: () => {
      start.current = null;
    },
    onClickCapture: (e) => {
      if (performance.now() > swallowUntil.current) return;
      swallowUntil.current = 0;
      e.stopPropagation();
      e.preventDefault();
    },
  };
}
